/**
 * Canvas minimap: known terrain, territories and cities, own units and
 * rival units in sight, alert pings and the camera's view; click or drag
 * pans. Static layers are cached offscreen and rebuilt only as needed.
 */
import {
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import * as THREE from 'three';
import { axialToWorld, getHexTileMapKey, type HexTileMapKey, hexMapConfig } from '@/game/hexmap';
import {
  exploredColor,
  FOG_COLOR,
  TILE_COLORS,
  WHEAT_COLOR,
} from '@/game/hexmap/view/tiles/sharedMaterials';
import type { RuleSet } from '@/game/rules';
import {
  type Building,
  type Cell,
  isCity,
  type PlayerId,
  type SimState,
  type TileVisibility,
} from '@/game/sim';
import { knownTerritories } from '@/game/view/lastSeen';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { useSimFrame } from './app/simFrame';
import { CollapseToggle, usePersisted } from './persisted';

/** The map's side, in CSS px. */
const MINIMAP_SIZE = 176;
const TILE_R = hexMapConfig.hexMap.tile.radius;
const SQRT3 = Math.sqrt(3);
/** Territory and known buildings are redrawn at most this often (ticks) between vision changes. */
const BASE_EVERY_TICKS = 10;
/** Off-sight explored land: this far towards the 3D map's dimmed colour, so it still reads at this size. */
const EXPLORED_DIM = 0.45;

// Axial neighbour directions with the angle (degrees, screen y down = world +z)
// pointing at them; the edge they share is the two vertices 30° either side.
const DIRECTIONS: Array<[number, number, number]> = [
  [1, 0, 0],
  [0, 1, 60],
  [-1, 1, 120],
  [-1, 0, 180],
  [0, -1, 240],
  [1, -1, 300],
];

/** Unit pointy-top hex corners (cos, sin), so a redraw does no trigonometry per tile. */
const CORNERS: ReadonlyArray<readonly [number, number]> = Array.from({ length: 6 }, (_, i) => {
  const a = ((60 * i - 30) * Math.PI) / 180;
  return [Math.cos(a), Math.sin(a)] as const;
});

const dimmed = (color: string) =>
  `#${new THREE.Color(color).lerp(exploredColor(color), EXPLORED_DIM).getHexString()}`;
const TERRAIN_SEEN: Record<string, string> = {
  ...TILE_COLORS,
  // Fields read by their wheat, as on the map.
  field: WHEAT_COLOR,
};
const TERRAIN_DIM: Record<string, string> = Object.fromEntries(
  Object.entries(TERRAIN_SEEN).map(([type, color]) => [type, dimmed(color)])
);

interface MinimapProps {
  mapRadius: number;
  /** Urgent alerts' tiles (attacks, losses): pulsing pings. */
  pings: readonly Cell[];
  cameraRef: RefObject<THREE.Camera | null>;
  /** Height of the ground the view rectangle is cut from. */
  groundY: number;
  /** Centre the camera on a world point. */
  onPan: (x: number, z: number) => void;
}

/**
 * Glass-panel minimap: terrain as the player knows it (fog where unexplored,
 * dimmed out of sight), every known territory tinted and outlined in its
 * owner's colour, known cities, own units and rival units in sight as dots,
 * the camera's view, and pings on urgent alerts. Click or drag pans there.
 * Fog-safe: it reads the same views as the 3D map. The terrain, territory
 * and cities are cached on an offscreen canvas rebuilt on vision changes
 * (and every BASE_EVERY_TICKS); each tick only blits it and draws the units.
 */
export function Minimap(props: MinimapProps) {
  const [collapsed, setCollapsed] = usePersisted('minimapCollapsed', false);
  return (
    <div className="panel pointer-events-auto relative shrink-0 self-start p-1.5">
      {!collapsed && <MinimapCanvas {...props} />}
      <div
        className={
          collapsed
            ? 'flex items-center gap-1 pl-1.5 text-xs'
            : 'absolute right-1 top-1 rounded-full bg-surface-strong'
        }
      >
        {collapsed && <span className="text-fg-muted">Map</span>}
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          label="minimap"
        />
      </div>
    </div>
  );
}

function MinimapCanvas({ mapRadius, pings, cameraRef, groundY, onPan }: MinimapProps) {
  // The viewer's state (viewerState.ts) and fog, as the 3D view draws them.
  const frame = useSimFrame();
  const { terrain } = frame;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef<SVGPolygonElement | null>(null);
  const baseRef = useRef<HTMLCanvasElement | null>(null);
  const baseBuiltRef = useRef<{ vision: number; tick: number } | null>(null);
  const dpr = typeof window === 'undefined' ? 1 : Math.min(2, window.devicePixelRatio || 1);
  const px = MINIMAP_SIZE * dpr;
  // World units → CSS px, fitting the map's width with a small margin.
  const scale = (MINIMAP_SIZE / 2 - 3) / (SQRT3 * TILE_R * (mapRadius + 0.5));
  const toMap = (x: number, z: number): [number, number] => [
    MINIMAP_SIZE / 2 + x * scale,
    MINIMAP_SIZE / 2 + z * scale,
  ];

  // Tile centres on the map, computed once per terrain.
  const centres = useMemo(() => {
    const out: Array<{ key: HexTileMapKey; type: string; x: number; y: number }> = [];
    for (const [key, tile] of terrain) {
      const w = axialToWorld(tile.coordinates, TILE_R);
      out.push({
        key,
        type: tile.type,
        x: MINIMAP_SIZE / 2 + w.x * scale,
        y: MINIMAP_SIZE / 2 + w.z * scale,
      });
    }
    return out;
  }, [terrain, scale]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const { view: state, playerId, playerOrder, rules, fog } = frame;
    const built = baseBuiltRef.current;
    if (
      !baseRef.current ||
      !built ||
      built.vision !== fog.version ||
      state.tick - built.tick >= BASE_EVERY_TICKS ||
      state.tick < built.tick
    ) {
      baseRef.current = drawBase(baseRef.current, px, dpr, scale, centres, {
        state,
        playerId,
        playerOrder,
        rules,
        // Sight alone, for the tiles themselves and borders.
        getTileSight: fog.sight,
        known: frame.knownBuildings(),
      });
      baseBuiltRef.current = { vision: fog.version, tick: state.tick };
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, px, px);
    ctx.drawImage(baseRef.current, 0, 0);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Units: rivals first, so own dots stay on top.
    const units = Object.values(state.units).filter(
      (u) =>
        !u.inside &&
        // Sight plus revealed attackers' tiles: whether a unit shows.
        (u.owner === playerId || fog.visibility(getHexTileMapKey(u.pos)) === 'visible')
    );
    units.sort((a, b) => Number(a.owner === playerId) - Number(b.owner === playerId));
    ctx.lineWidth = 0.75;
    ctx.strokeStyle = 'rgba(0,0,0,0.6)';
    for (const u of units) {
      const w = axialToWorld(u.pos, TILE_R);
      const [x, y] = [MINIMAP_SIZE / 2 + w.x * scale, MINIMAP_SIZE / 2 + w.z * scale];
      ctx.fillStyle = PLAYER_COLORS[colorIndexFor(u.owner, playerOrder)];
      ctx.beginPath();
      ctx.arc(x, y, 1.7, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }, [frame, centres, px, dpr, scale]);

  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  // The camera's view: the ground under the screen's corners, tracked per
  // frame but written to the DOM only when it moves.
  useEffect(() => {
    let raf = 0;
    let last = '';
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -groundY);
    const ray = new THREE.Ray();
    const hit = new THREE.Vector3();
    const corners: Array<[number, number]> = [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ];
    const frame = () => {
      raf = requestAnimationFrame(frame);
      const camera = cameraRef.current;
      const polygon = viewRef.current;
      if (!camera || !polygon) return;
      const points: string[] = [];
      for (const [nx, ny] of corners) {
        ray.origin.setFromMatrixPosition(camera.matrixWorld);
        ray.direction.set(nx, ny, 0.5).unproject(camera).sub(ray.origin).normalize();
        if (!ray.intersectPlane(plane, hit)) return;
        const [x, y] = [
          MINIMAP_SIZE / 2 + hit.x * scaleRef.current,
          MINIMAP_SIZE / 2 + hit.z * scaleRef.current,
        ];
        points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
      }
      const next = points.join(' ');
      if (next !== last) {
        last = next;
        polygon.setAttribute('points', next);
      }
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [cameraRef, groundY]);

  // Click or drag: the camera goes to the point under the pointer.
  const draggingRef = useRef(false);
  const panAt = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - MINIMAP_SIZE / 2) / scale;
    const z = (e.clientY - rect.top - MINIMAP_SIZE / 2) / scale;
    onPan(x, z);
  };

  return (
    <div
      className="relative cursor-crosshair touch-none overflow-hidden rounded-xl"
      style={{ width: MINIMAP_SIZE, height: MINIMAP_SIZE }}
      title="Click or drag to move the view"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        draggingRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        panAt(e);
      }}
      onPointerMove={(e) => {
        if (draggingRef.current) panAt(e);
      }}
      onPointerUp={() => {
        draggingRef.current = false;
      }}
      onPointerCancel={() => {
        draggingRef.current = false;
      }}
    >
      <canvas
        ref={canvasRef}
        width={px}
        height={px}
        style={{ width: MINIMAP_SIZE, height: MINIMAP_SIZE }}
        className="block"
      />
      <svg
        className="pointer-events-none absolute inset-0"
        width={MINIMAP_SIZE}
        height={MINIMAP_SIZE}
        aria-hidden="true"
      >
        <polygon
          ref={viewRef}
          className="fill-fg/10 stroke-fg"
          strokeWidth={1}
          strokeLinejoin="round"
        />
      </svg>
      {[...new Map(pings.map((c) => [`${c.q},${c.r}`, c])).values()].map((cell) => {
        const w = axialToWorld(cell, TILE_R);
        const [x, y] = toMap(w.x, w.z);
        return (
          <span
            key={`${cell.q},${cell.r}`}
            className="pointer-events-none absolute"
            style={{ left: x, top: y }}
          >
            <span className="absolute -left-2 -top-2 h-4 w-4 animate-ping rounded-full bg-danger" />
            <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full border border-surface-strong bg-danger" />
          </span>
        );
      })}
    </div>
  );
}

interface BaseInputs {
  state: SimState;
  playerId: PlayerId;
  playerOrder: readonly PlayerId[];
  rules: RuleSet;
  getTileSight: (key: HexTileMapKey) => TileVisibility;
  known: Building[];
}

/** One hex's outline (pointy-top, like the map) added to the current path. */
function hexPath(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.moveTo(x + r * CORNERS[0][0], y + r * CORNERS[0][1]);
  for (let i = 1; i < 6; i++) ctx.lineTo(x + r * CORNERS[i][0], y + r * CORNERS[i][1]);
  ctx.closePath();
}

/** Terrain, territory tints and borders, and known cities, onto `canvas` (made if missing). */
function drawBase(
  canvas: HTMLCanvasElement | null,
  px: number,
  dpr: number,
  scale: number,
  centres: ReadonlyArray<{ key: HexTileMapKey; type: string; x: number; y: number }>,
  { state, playerId, playerOrder, rules, getTileSight, known }: BaseInputs
): HTMLCanvasElement {
  const out = canvas ?? document.createElement('canvas');
  out.width = px;
  out.height = px;
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const r = TILE_R * scale;
  // A hair larger than the tile, so neighbours meet without seams.
  const fillR = r + 0.35;
  const centreOf = new Map(centres.map((c) => [c.key, c]));

  // Terrain, one path per colour.
  const byColor = new Map<string, Array<{ x: number; y: number }>>();
  for (const c of centres) {
    const sight = getTileSight(c.key);
    // Unexplored water is fog too, so the coast never shows before it's found.
    const color =
      sight === 'unexplored'
        ? FOG_COLOR
        : ((sight === 'visible' ? TERRAIN_SEEN[c.type] : TERRAIN_DIM[c.type]) ?? FOG_COLOR);
    const list = byColor.get(color) ?? [];
    list.push(c);
    byColor.set(color, list);
  }
  for (const [color, list] of byColor) {
    ctx.fillStyle = color;
    ctx.beginPath();
    for (const c of list) hexPath(ctx, c.x, c.y, fillR);
    ctx.fill();
  }

  // Territories as the player knows them, tinted and outlined.
  const territories = knownTerritories(state, known, playerId, rules, getTileSight);
  for (const [owner, tiles] of territories) {
    const color = PLAYER_COLORS[colorIndexFor(owner, playerOrder)];
    ctx.globalAlpha = owner === playerId ? 0.22 : 0.3;
    ctx.fillStyle = color;
    ctx.beginPath();
    for (const key of tiles) {
      const c = centreOf.get(key);
      if (c) hexPath(ctx, c.x, c.y, fillR);
    }
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    for (const key of tiles) {
      const c = centreOf.get(key);
      if (!c) continue;
      const [q, rr] = key.split(',').map(Number);
      for (const [dq, dr, angle] of DIRECTIONS) {
        if (tiles.has(`${q + dq},${rr + dr}` as HexTileMapKey)) continue;
        // The edge facing `angle` runs between the corners at angle ∓ 30°.
        const [x0, y0] = CORNERS[angle / 60];
        const [x1, y1] = CORNERS[(angle / 60 + 1) % 6];
        ctx.moveTo(c.x + r * x0, c.y + r * y0);
        ctx.lineTo(c.x + r * x1, c.y + r * y1);
      }
    }
    ctx.stroke();
  }

  // Known cities: a square in the owner's colour.
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(0,0,0,0.7)';
  for (const b of known) {
    if (!isCity(b, rules)) continue;
    const c = centreOf.get(getHexTileMapKey(b.pos));
    if (!c) continue;
    ctx.fillStyle = PLAYER_COLORS[colorIndexFor(b.owner, playerOrder)];
    ctx.fillRect(c.x - 2.5, c.y - 2.5, 5, 5);
    ctx.strokeRect(c.x - 2.5, c.y - 2.5, 5, 5);
  }
  return out;
}
